/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatRadioChange } from '@angular/material/radio';
import { ActivatedRoute } from '@angular/router';

import { BuilderUserContext, invoiceNumberSorting } from '@gh/builder-shared';
import { BuilderPermission, DrawInvoiceStatus, InvoiceType } from '@gh/builder-shared-data';
import {
  CELL_NO_OVERFLOW_CLASS,
  CELL_TEXT_RIGHT_CLASS,
  defaultCurrencyCellRendererDecorator,
  dictionaryValueFormatter,
  outerLabelCellRendererDecorator,
  ROW_DELETED_CLASS,
} from '@gh/common-internal-shared';

import {
  AppRouter,
  DateTimeTypes,
  DictionaryService,
  EnumDictionary,
  getRouteInitData,
  IS_REGION_US,
  NumberTypes,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  buildDateTimeColumn,
  buildNumberColumn,
  GridDefinitionFactory,
  GridRowEvent,
  LayoutBasedGridDefinition,
  ManualGridDefinition,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import { isNotNil, openInNewWindow } from '@gh/core-util';

import { get, head, includes } from 'lodash';

import {
  calculateDrawTotals,
  jobDrawsInvoiceNumberComparator,
  JobFinancialsDraw,
  JobFinancialsDrawPayment,
  JobFinancialsDrawsFilterCode,
  JobFinancialsDrawTotal,
  JobFinancialsInvoicePlacement,
} from '../../../resources/job-financials.model';
import { JobDrawsDetailsInvoice } from '../../../resources/job-details-tab';

@Component({
  selector: 'gh-job-financials-draw-list',
  template: require('./job-financials-draw-list.component.html'),
  styles: [require('./job-financials-draw-list.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobFinancialsDrawListComponent implements OnInit, OnChanges {
  drawTotals: JobFinancialsDrawTotal[];
  drawsGridDefinition: ManualGridDefinition;
  jobFinancialsPaymentList: JobFinancialsDrawPayment[] = [];
  paymentsGridDefinition: LayoutBasedGridDefinition;
  jobFinancialsDrawsFilterCode = JobFinancialsDrawsFilterCode;
  selectedDrawId: Maybe<ID>;

  isRegionUs = IS_REGION_US;

  @Input() jobFinancialsDrawList: JobFinancialsDraw[];
  @Input() jobId: ID;
  @Input() filter: JobFinancialsDrawsFilterCode = JobFinancialsDrawsFilterCode.isOnlyActiveDraws;
  @Input() invoiceTypes: EnumDictionary<InvoiceType>;
  @Input() invoiceStatuses: EnumDictionary<DrawInvoiceStatus>;

  @Output() filterChange = new EventEmitter<string>();

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private userContext: BuilderUserContext,
              private translationService: TranslationService,
              private dictionaryService: DictionaryService,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    const { paymentsLayout } = getRouteInitData(this.route);
    this.drawsGridDefinition = this.gridDefinitionFactory.buildManual([{
      field: 'invoiceNumber',
      width: 200,
      headerName: this.translationService.get('lbl_invoice_number'),
      valueFormatter: ({ value }) => value || '',
      comparator: invoiceNumberSorting,
    }, {
      field: 'accountingDate',
      width: 200,
      headerName: this.translationService.get('lbl_date'),
      ...buildDateTimeColumn(DateTimeTypes.ShortDate),
    }, {
      field: 'type',
      width: 200,
      headerName: this.translationService.get('lbl_type'),
      valueFormatter: dictionaryValueFormatter(this.invoiceTypes),
    }, {
      field: 'description',
      width: 200,
      headerName: this.translationService.get('lbl_description'),
    }, {
      field: 'amountWithGst',
      width: 200,
      cellClassRules: {
        [CELL_NO_OVERFLOW_CLASS]: 'true',
        [CELL_TEXT_RIGHT_CLASS]: 'true',
      },
      headerName: this.translationService.get('lbl_amount_dollar'),
      ...buildNumberColumn(NumberTypes.Price),
      pinnedRowCellRenderer: outerLabelCellRendererDecorator(
        ({ data }) => this.getTotalLabel(data))(
        defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice))),
    }, {
      field: 'balance',
      width: 200,
      headerName: this.translationService.get('lbl_balance_dollar'),
      ...buildNumberColumn(NumberTypes.Price),
      pinnedRowCellRenderer: defaultCurrencyCellRendererDecorator(typedValueFormatter(NumberTypes.SignedPrice)),
    },
    {
      field: 'status',
      width: 200,
      headerName: this.translationService.get('lbl_status'),
      cellClass: ({ data }: { data: JobDrawsDetailsInvoice }) => ({
        [DrawInvoiceStatus.Draft]: 'cell--unimportant',
        [DrawInvoiceStatus.Submitted]: 'cell--unimportant',
        [DrawInvoiceStatus.Authorized]: 'cell--positive',
        [DrawInvoiceStatus.Paid]: 'cell--positive',
        [DrawInvoiceStatus.Deleted]: 'cell--negative',
        [DrawInvoiceStatus.Voided]: 'cell--negative',
      }[data.status]),
      valueFormatter: dictionaryValueFormatter(this.invoiceStatuses),
    }],                                                               {
      rowSelection: 'single',
      rowClassRules: {
        [ROW_DELETED_CLASS]: ({ data }: { data: JobFinancialsDraw }) =>
          !includes([DrawInvoiceStatus.Authorized, DrawInvoiceStatus.Paid], data.status)
          && !includes(data.invoiceId, 'total:'),
      },
      getRowNodeId: ({ invoiceId }: JobFinancialsDraw) => invoiceId,
    });
    this.paymentsGridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      amount: buildNumberColumn(NumberTypes.Price),
      paidDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
    },                                                                        {
      getRowNodeId: ({ paymentId }: JobFinancialsDrawPayment) => paymentId,
    });
    this.paymentsGridDefinition.bind(paymentsLayout);
    this.drawTotals = calculateDrawTotals(this.jobFinancialsDrawList);
    this.jobFinancialsPaymentList = get(head(this.jobFinancialsDrawList), 'payments', []);
    this.selectedDrawId = get(head(this.jobFinancialsDrawList), 'invoiceId', void 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('jobFinancialsDrawList')) {
      this.drawTotals = calculateDrawTotals(this.jobFinancialsDrawList);
      this.jobFinancialsPaymentList = get(head(this.jobFinancialsDrawList), 'payments', []);
      this.selectedDrawId = get(head(this.jobFinancialsDrawList), 'invoiceId', void 0);
    }
  }

  onTypeFilterChange({ value }: MatRadioChange): void {
    this.filterChange.emit(value);
  }

  onDrawRowClicked(event: GridRowEvent<JobFinancialsDraw>): void {
    if (!includes(event.data.invoiceId, 'total:')) {
      this.jobFinancialsPaymentList = event.data.payments;
    }
  }

  onDrawOpen({ invoiceId, variationId, type, isCreatedFromChangeOrder }: JobFinancialsDraw): void {
    const routeParentTitleLabel = 'lbl_job_financials';
    if (type === InvoiceType.Draw) {
      this.router.navigateToChild(['accounting', 'jobs', this.jobId, 'draws', invoiceId], {
        parentTitle: routeParentTitleLabel,
      });
    } else if (type === InvoiceType.ChangeOrder) {
      if (isCreatedFromChangeOrder) {
        this.router.navigateToChild(['job-admin', 'jobs', this.jobId, 'change-orders', variationId, 'invoice'], {
          parentTitle: routeParentTitleLabel,
        });
      } else {
        this.router.navigateToChild(['accounting', 'jobs', this.jobId, 'draws', invoiceId], {
          parentTitle: routeParentTitleLabel,
        });
      }
    }
  }

  hasDrawOpenPermissions(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Accounting.Draws.PRODUCE)
      || this.userContext.hasPermission(BuilderPermission.Accounting.Draws.VIEW);
  }

  canOpenDraw({ isNotOpening }: JobFinancialsDraw): boolean {
    return this.hasDrawOpenPermissions() && isNotNil(isNotOpening) && !isNotOpening;
  }

  hasXeroOpenPermissions(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Accounting.Draws.PRODUCE)
      || this.userContext.hasPermission(BuilderPermission.Accounting.Draws.VIEW);
  }

  isXeroOpenExists(draw: JobFinancialsDraw): boolean {
    return this.hasXeroOpenPermissions()
      && this.isDrawContainsXeroUrl(draw)
      && isNotNil(draw.externalId);
  }

  isDrawContainsXeroUrl({ xeroUrl }: JobFinancialsDraw): boolean {
    return !!xeroUrl;
  }

  onXeroOpen({ xeroUrl }: JobFinancialsDraw): void {
    if (xeroUrl) {
      openInNewWindow(xeroUrl);
    }
  }

  private getTotalLabel(invoice: JobFinancialsDrawTotal): string {
    switch (invoice.placement) {
      case JobFinancialsInvoicePlacement.TotalContract:
        return this.translationService.get('lbl_contract');
      case JobFinancialsInvoicePlacement.TotalChangeOrders:
        return this.translationService.get('lbl_variations');
      case JobFinancialsInvoicePlacement.TotalDraws:
        return this.translationService.get('lbl_total_draws');
      case JobFinancialsInvoicePlacement.TotalCrNotes:
        return this.translationService.get('lbl_cr_notes');
      default:
        return '';
    }
  }
}
